import { FC, useState, KeyboardEvent, useRef } from "react";
import { FaFolder, FaCog } from "react-icons/fa";
import VariableChip from "./VariableChip";
import InputFrame from "../../atoms/InputFrame";

type AttachmentConfig = {
    filename: string;
    type: 'link' | 'file';
    expirationMinutes?: number;
};

type AttachmentInputProps = {
    label: string;
    value: AttachmentConfig[];
    onChange: (values: AttachmentConfig[]) => void;
    errorMessage?: string;
};

const AttachmentInput: FC<AttachmentInputProps> = ({
    label,
    value,
    onChange,
    errorMessage,
}) => {
    const [inputValue, setInputValue] = useState("");
    const [showConfig, setShowConfig] = useState(false);
    const [attachmentType, setAttachmentType] = useState<'link' | 'file'>('file');
    const [expirationMinutes, setExpirationMinutes] = useState(60);
    const inputRef = useRef<HTMLInputElement>(null);

    const addValue = (val: string) => {
        const trimmed = val.trim();
        if (trimmed && !value.find(v => v.filename === trimmed)) {
            const newAttachment: AttachmentConfig = {
                filename: trimmed,
                type: attachmentType,
                ...(attachmentType === 'link' && { expirationMinutes })
            };
            onChange([...value, newAttachment]);
        }
        setInputValue("");
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Tab' || e.key === 'Enter') {
            if (inputValue.trim()) {
                e.preventDefault();
                addValue(inputValue);
            }
        } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
            onChange(value.slice(0, -1));
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;

        if (val.includes(',') || val.includes(';')) {
            const parts = val.split(/[,;]/);
            parts.slice(0, -1).forEach(part => {
                const trimmed = part.trim();
                if (trimmed && !value.find(v => v.filename === trimmed)) {
                    const newAttachment: AttachmentConfig = {
                        filename: trimmed,
                        type: attachmentType,
                        ...(attachmentType === 'link' && { expirationMinutes })
                    };
                    onChange([...value, newAttachment]);
                }
            });
            setInputValue(parts[parts.length - 1]);
        } else if (val.endsWith('}}')) {
            addValue(val);
        } else {
            setInputValue(val);
        }
    };

    const handleBrowseFiles = () => {
        alert("Buscar arquivos no bucket - funcionalidade em desenvolvimento");
    };

    const isVariable = (text: string) => text.startsWith("{{") && text.endsWith("}}");

    const error = Boolean(errorMessage);
    const borderColor = error ? "border-red-600 dark:border-red-400" : "border-gray-300 dark:border-gray-600";

    return (
        <InputFrame label={label} errorMessage={errorMessage}>
            <div className="w-full">
                <div className="flex items-center gap-2 mb-2">
                    <button
                        type="button"
                        onClick={handleBrowseFiles}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        title="Buscar arquivos no bucket"
                    >
                        <FaFolder className="w-4 h-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowConfig(!showConfig)}
                        className={`text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 ${showConfig ? 'text-blue-600 dark:text-blue-400' : ''}`}
                        title="Configurar tipo de anexo"
                    >
                        <FaCog className="w-4 h-4" />
                    </button>
                    <div className="flex-1 flex flex-wrap gap-2 items-center">
                        {value.map((item, index) => (
                            <VariableChip
                                key={index}
                                value={item.filename}
                                isVariable={isVariable(item.filename)}
                                onRemove={() => onChange(value.filter((_, i) => i !== index))}
                                format="fileUrl"
                                customIcon={!isVariable(item.filename) ? <span>{item.type === 'link' ? '🔗' : '📎'}</span> : undefined}
                                title={item.type === 'link' ? `Link (${item.expirationMinutes}min)` : 'Arquivo'}
                            />
                        ))}
                        <input
                            ref={inputRef}
                            type="text"
                            value={inputValue}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            placeholder={value.length === 0 ? "arquivo.pdf ou {{variavel}}" : ""}
                            className={`flex-1 min-w-[120px] py-2.5 px-0 text-sm bg-transparent border-0 border-b-2 ${borderColor} appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 dark:text-white dark:placeholder-gray-400`}
                        />
                    </div>
                </div>

                {showConfig && (
                    <div className="flex gap-4 items-center p-2 bg-gray-50 dark:bg-slate-800 rounded text-xs">
                        <label className="flex items-center gap-2">
                            <input
                                type="radio"
                                value="file"
                                checked={attachmentType === 'file'}
                                onChange={(e) => setAttachmentType(e.target.value as 'file')}
                            />
                            <span className="dark:text-white">Arquivo anexo</span>
                        </label>
                        <label className="flex items-center gap-2">
                            <input
                                type="radio"
                                value="link"
                                checked={attachmentType === 'link'}
                                onChange={(e) => setAttachmentType(e.target.value as 'link')}
                            />
                            <span className="dark:text-white">Link temporário</span>
                        </label>
                        {attachmentType === 'link' && (
                            <label className="flex items-center gap-2">
                                <span className="dark:text-white">Expiração:</span>
                                <input
                                    type="number"
                                    value={expirationMinutes}
                                    onChange={(e) => setExpirationMinutes(Number(e.target.value))}
                                    min={1}
                                    max={10080}
                                    className="w-16 px-2 py-1 border border-gray-300 rounded dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                                />
                                <span className="dark:text-white">min</span>
                            </label>
                        )}
                    </div>
                )}
            </div>
        </InputFrame>
    );
};

export default AttachmentInput;
